<template>
    <div v-if="displayItems.length" class="referral-context-menu__dropdown-menus referral-actions">
        <span
            v-for="(item, index) of displayItems"
            :key="index"
            class="referral-context-menu__menu-item"
            :class="{ 'disabled-item': item.disabled }"
            @click="!item.disabled && executeItem(item)"
        >
            <common-icon v-if="item.icon" :name="item.icon" size="small"></common-icon>

            <span>{{ item.label }}</span>
        </span>
    </div>
</template>

<script>
    import ReferralStatusMixin from 'mixins/ReferralStatusMixin'
    import UsersAPI from 'api/UsersAPI'
    import ReferralConfirmMixin from 'mixins/ReferralConfirmMixin'
    import cloneDeep from 'lodash.clonedeep'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import ReferralStatusEnum from '@/enums/ReferralStatusEnum'
    import ReferralPathTypeEnum from '@/enums/ReferralPathTypeEnum'
    import ReferralTrackingTypeEnum from '@/enums/ReferralTrackingTypeEnum'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'

    export default {
        mixins: [ReferralStatusMixin, ReferralConfirmMixin, ScreenSizeDetectionMixin],

        props: {
            optionProp: { type: Object, required: true },
            asContextMenu: { type: Boolean, default: false },
            showCancelled: { type: Boolean, default: false },
            type: { type: String, default: null },
        },

        data() {
            return {
                ReferralPathTypeEnum,
                option: {},
            }
        },

        computed: {
            isUnavailable() {
                return !!this.option.value.isUnavailable
            },

            referral() {
                return this.option.value.referral
            },

            referralId() {
                return (this.referral || {}).id || 0
            },

            isReferringPhysician() {
                return this.type === ReferralPathTypeEnum.MADE
            },

            allItems() {
                return [
                    {
                        isValid: this.asContextMenu && this.isTablet,
                        label: this.$t('pages.referrals.index.referrals_made.detail'),
                        name: 'open-details',
                        icon: 'eye',
                    },
                    {
                        isValid:
                            !this.asContextMenu && (this.isReferralOk(this.referral) || this.isReferralPending(this.referral)),
                        label: this.referralStatusDescription,
                        name: 'status',
                        class: 'plain',
                        icon: null,
                    },
                    {
                        isValid:
                            !this.asContextMenu &&
                            !!this.referral &&
                            !(this.isReferralCancelledOrNoShow(this.referral) && !this.showCancelled),
                        label: this.$t(`pages.referrals.new.dropdown.label.view_referral`),
                        name: 'view',
                        route: this.getRoute(),
                        icon: 'eye',
                    },
                    {
                        isValid: this.asContextMenu && this.shouldShowReferralDetails(this.referral),
                        label: this.$t(`pages.referrals.new.dropdown.label.print_prescription`),
                        name: 'downloadPdf',
                        icon: 'download',
                    },
                    {
                        isValid: this.asContextMenu && this.shouldChooseSlot(this.referral),
                        label: this.$t(`pages.referrals.new.dropdown.label.referring.choose_slot`),
                        name: 'choose-slot',
                        icon: 'check',
                    },
                    {
                        isValid: this.asContextMenu && this.shouldUploadRequestedRecords(this.referral),
                        label: this.$t('pages.referrals.new.dropdown.label.referring.upload_requested_records'),
                        name: 'upload-additional-records',
                        icon: 'document',
                    },
                    {
                        isValid: this.asContextMenu && this.shouldProposeSlot(this.referral),
                        label: this.$t(`pages.referrals.new.dropdown.label.referred.propose_slot`),
                        name: 'propose-slot',
                        icon: 'calendar',
                    },
                    {
                        isValid: this.shouldProposeSlot(this.referral),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.decline'),
                        name: 'external-decline',
                        icon: 'close',
                    },
                    {
                        isValid: this.shouldProposeSlot(this.referral),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.additional_records'),
                        name: 'request-additional-records',
                        icon: 'attach',
                    },
                    {
                        isValid: this.asContextMenu && this.isReferralReschedulable(this.referral),
                        label: this.$t(`pages.referrals.new.dropdown.label.follow_up_visit`),
                        name: 'follow-up-visit',
                        icon: 'calendar',
                    },
                    {
                        isValid:
                            !this.isReferringPhysician &&
                            this.isReferralPending(this.referral) &&
                            this.referral.sourceType === ReferralTrackingTypeEnum.DIRECT &&
                            !this.isAppointmentInPast(this.referral),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.confirm'),
                        name: 'confirm',
                        route: this.getRoute('confirm'),
                        icon: 'check',
                    },
                    {
                        isValid:
                            !this.isReferringPhysician &&
                            this.referral &&
                            this.referral.sourceType === ReferralTrackingTypeEnum.DIRECT &&
                            (this.isReferralPending(this.referral) ||
                                (this.isReferralToBeRescheduled(this.referral) && !this.isReferralConfirmed(this.referral))),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.decline'),
                        name: 'decline',
                        route: this.getRoute('decline'),
                        icon: 'close',
                    },
                    {
                        isValid:
                            !this.isReferringPhysician &&
                            this.referral &&
                            this.referral.sourceType === ReferralTrackingTypeEnum.DIRECT &&
                            ((this.isReferralWaitingForPatient(this.referral) && !this.isAppointmentInPast(this.referral)) ||
                                this.isReferralWaitingForInsurance(this.referral) ||
                                this.isReferralPending(this.referral) ||
                                this.isReferralToBeRescheduled(this.referral) ||
                                this.isReferralPendingReschedule(this.referral)),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.reschedule'),
                        name: 'reschedule',
                        route: this.getRoute('reschedule'),
                        icon: 'calendar',
                    },
                    {
                        isValid:
                            !this.isReferringPhysician &&
                            this.referral &&
                            this.referral.sourceType === ReferralTrackingTypeEnum.DIRECT &&
                            ((this.isReferralWaitingForPatient(this.referral) && !this.isAppointmentInPast(this.referral)) ||
                                this.isReferralWaitingForInsurance(this.referral) ||
                                (this.isReferralToBeRescheduled(this.referral) && this.isReferralConfirmed(this.referral))),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.cancel'),
                        name: 'cancel',
                        route: this.getRoute('cancel'),
                        icon: 'close',
                    },
                    {
                        isValid:
                            !this.isReferringPhysician &&
                            this.referral &&
                            this.referral.sourceType === ReferralTrackingTypeEnum.DIRECT &&
                            (this.isReferralWaitingForReport(this.referral) || this.isReferralCompleted(this.referral)),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.add_report'),
                        name: 'addReport',
                        route: { name: `referrals.received.add-report`, params: { id: this.referralId } },
                        icon: 'file',
                    },
                    {
                        isValid:
                            this.isReferringPhysician &&
                            this.referral &&
                            this.referral.sourceType === ReferralTrackingTypeEnum.MATCH &&
                            this.isReferralWaitingForReport(this.referral),
                        label: this.$t('pages.referrals.new.dropdown.label.referring.request_report'),
                        name: 'requestReport',
                        icon: 'file',
                    },
                    {
                        isValid:
                            this.isReferringPhysician &&
                            this.referral &&
                            this.referral.sourceType === ReferralTrackingTypeEnum.DIRECT &&
                            !this.isReferralWaitingForReport(this.referral) &&
                            !this.isReferralCancelledOrNoShow(this.referral),
                        label: this.$t('pages.referrals.new.dropdown.label.referring.cancel'),
                        name: 'cancel',
                        icon: 'close',
                    },
                    {
                        isValid:
                            this.asContextMenu &&
                            !this.isReferringPhysician &&
                            this.referral &&
                            this.referral.sourceType === ReferralTrackingTypeEnum.DIRECT &&
                            this.isAppointmentInPast(this.referral) &&
                            (this.isReferralWaitingForPatient(this.referral) || this.isReferralCancelledOrNoShow(this.referral)),
                        label: this.$t('pages.referrals.new.dropdown.label.referred.patient_seen'),
                        name: 'patientSeen',
                        route: this.getRoute(),
                        icon: 'check',
                    },
                    {
                        isValid: this.isReferringPhysician && this.isReferralError(this.referral),
                        label: this.$t('pages.referrals.new.dropdown.label.referring.match'),
                        name: 'match',
                        icon: 'link',
                    },
                    {
                        isValid:
                            !this.asContextMenu &&
                            (!this.referral || (this.isReferralCancelledOrNoShow(this.referral) && !this.showCancelled)) &&
                            !this.isUnavailable,
                        label: this.$t(`pages.referrals.new.dropdown.label.set_unavailable`),
                        fn: this.setUnavailable,
                        icon: 'close',
                    },
                    {
                        isValid:
                            !this.asContextMenu &&
                            (this.$auth.isPracticePremium() || this.$auth.isPracticeTrial()) &&
                            (!this.referral || (this.isReferralCancelledOrNoShow(this.referral) && !this.showCancelled)),
                        label: this.$t(`pages.referrals.new.dropdown.label.create_referral`),
                        fn: this.createReferral,
                        disabled: this.isUnavailable,
                        icon: 'plus-circle',
                    },
                    {
                        isValid: !this.asContextMenu && !this.referral && this.isUnavailable,
                        label: this.$t(`pages.referrals.new.dropdown.label.set_available`),
                        fn: this.setAvailable,
                        icon: 'check',
                    },
                    {
                        isValid:
                            !this.asContextMenu &&
                            !!this.referral &&
                            !(this.isReferralCancelledOrNoShow(this.referral) && !this.showCancelled),
                        label: this.$t(`pages.referrals.new.dropdown.label.patient_visit`),
                        name: 'patient-profile',
                        fn: this.goToPatientProfile,
                        icon: 'user',
                    },
                ]
            },

            displayItems() {
                return this.allItems.filter((item) => item.isValid)
            },

            referralStatusDescription() {
                if (!this.referral) return ''
                return this.$t(`pages.referrals.tracking.status_description.${this.referral.status}`)
            },

            slotData() {
                return {
                    userId: this.$auth.getActiveMember().id,
                    date: this.option.value.date,
                    startTime: this.option.value.startTime,
                    endTime: this.option.value.endTime,
                    practiceId: this.$auth.getPractice().id,
                    practiceLocationId: Number(this.$route.query.locationId) || 0,
                }
            },
        },

        methods: {
            goToPatientProfile() {
                this.$router.push(`/patients/details/patients/${this.referral.patientId}`)
            },
            getRoute(action, actionAs = 'query') {
                let route = { name: `referrals.received.show`, params: { id: this.referralId } }
                if (action) {
                    route[actionAs] = { action: action }
                }
                return route
            },

            executeItem(item) {
                if (this.asContextMenu) {
                    const isProposeSlot = item.name === 'choose-slot' || item.name === 'propose-slot'
                    this.emitEvent({
                        name: item.name,
                        payload: {
                            ...(isProposeSlot && { referralId: this.referral.id }),
                            isReferringPhysician: this.isReferringPhysician,
                        },
                    })
                    this.emitEvent({ name: 'closeReferralContextMenu' })
                } else if (item.fn) {
                    let that = this
                    Promise.resolve()
                        .then(() => item.fn())
                        .then(() => {
                            that.emitEvent({ name: 'closeReferralContextMenu' })
                        })
                } else if (item.route) {
                    this.$router.push(item.route)
                }
            },

            setUnavailable() {
                return UsersAPI.setAvailabilitySlotUnavailable(this.slotData).then(() => {
                    this.option.value.isUnavailable = true
                    this.emitEvent({ name: 'schedule-changed' })
                })
            },

            createReferral() {
                if (this.$auth.getPractice().practiceType === PracticeTypeEnum.PHYSICIAN) {
                    const query = {
                        userId: this.slotData.userId,
                        practiceType: this.$auth.getPractice().practiceType,
                        searchBy: 'zipcode',
                        insurancePlanId: null,
                        specialityId: this.$auth.getActiveMember().specialities[0].speciality.id,
                        subSpecialityId: this.$auth.getActiveMember().specialities[0].subSpeciality.id,
                        physicianId: this.slotData.userId,
                        practiceId: this.slotData.practiceId,
                        practiceLocationId: this.slotData.practiceLocationId,
                        startTime: this.slotData.startTime,
                        endTime: this.slotData.endTime,
                        date: this.slotData.date,
                        selfReferred: true,
                    }

                    this.$router.push({
                        name: `referrals.new.make`,
                        query: query,
                    })
                } else {
                    const query = {
                        practiceLocationId: this.slotData.practiceLocationId,
                        practiceType: this.$auth.getPractice().practiceType,
                        practiceId: this.slotData.practiceId,
                        practiceName: this.$auth.getPractice().name,
                        startTime: this.slotData.startTime,
                        endTime: this.slotData.endTime,
                        date: this.slotData.date,
                        selfReferred: true,
                    }
                    this.$router.push({
                        name: `referrals.services.tests`,
                        query: query,
                    })
                }
            },

            setAvailable() {
                return UsersAPI.setAvailabilitySlotAvailable(this.slotData).then(() => {
                    this.option.value.isUnavailable = false
                    this.emitEvent({ name: 'schedule-changed' })
                })
            },

            shouldChooseSlot(referral) {
                return (
                    this.type &&
                    this.type === ReferralPathTypeEnum.MADE &&
                    referral &&
                    referral.sourceType === ReferralTrackingTypeEnum.MATCH &&
                    referral.status === ReferralStatusEnum.TIME_SLOTS_PROPOSED
                )
            },
            shouldProposeSlot(referral) {
                return (
                    this.type &&
                    this.type === ReferralPathTypeEnum.RECEIVED &&
                    referral &&
                    referral.sourceType === ReferralTrackingTypeEnum.MATCH &&
                    referral.status === ReferralStatusEnum.CREATED
                )
            },
            shouldUploadRequestedRecords(referral) {
                return (
                    this.type &&
                    this.type === ReferralPathTypeEnum.MADE &&
                    referral &&
                    referral.sourceType === ReferralTrackingTypeEnum.MATCH &&
                    referral.status === ReferralStatusEnum.CREATED
                )
            },
        },

        beforeMount() {
            this.option = cloneDeep(this.optionProp)
        },
    }
</script>
